import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Plus, ArrowLeft, Loader2 } from "lucide-react";
import useDebounce from "../../../hooks/useDebounce";
import { toast } from "sonner";
import { CedenteSearch } from "./CedenteSearch";
import { CedenteForm } from "./CedenteForm";
import { DealForm } from "./DealForm";
import { userService } from "../../../services/user.service";
import { processService } from "../../../services/process.service";
import { isValidCPF } from "../../../lib/utils";

const { getCedentes, createCedente } = userService;
const { linkCedente, checkDeletedLink } = processService;

function initDealData(editingItem) {
  if (!editingItem) {
    return {
      valor_credito: "", percentual_desagio: "",
      valor_parcelado: "", parcelas: "", valor_parcela: "",
      valor_sindicato: "", valor_contador: "", valor_proposta_final: "",
      valor_parcela_primeiro_periodo: "", valor_parcela_segundo_periodo: "", valor_parcela_terceiro_periodo: "",
      valor_fgts: "",
      data_primeira_parcela: "", data_ultima_parcela: "",
      tipo_aceite: null,
      valores_extras: {},
    };
  }
  return {
    valor_credito: editingItem.valor_credito != null ? String(editingItem.valor_credito) : "",
    percentual_desagio: editingItem.percentual_desagio != null ? String(editingItem.percentual_desagio) : "",
    valor_parcelado: editingItem.valor_parcelado != null ? String(editingItem.valor_parcelado) : "",
    parcelas: editingItem.parcelas != null ? String(editingItem.parcelas) : "",
    valor_parcela: editingItem.valor_parcela != null ? String(editingItem.valor_parcela) : "",
    valor_sindicato: editingItem.valores_extras?.honorarios_sindicato != null ? String(editingItem.valores_extras.honorarios_sindicato) : "",
    valor_contador: editingItem.valores_extras?.honorarios_contrat != null ? String(editingItem.valores_extras.honorarios_contrat) : "",
    valor_proposta_final: editingItem.valor_proposta_final != null ? String(editingItem.valor_proposta_final) : "",
    valor_parcela_primeiro_periodo: editingItem.valor_parcela_primeiro_periodo != null ? String(editingItem.valor_parcela_primeiro_periodo) : "",
    valor_parcela_segundo_periodo: editingItem.valor_parcela_segundo_periodo != null ? String(editingItem.valor_parcela_segundo_periodo) : "",
    valor_parcela_terceiro_periodo: editingItem.valor_parcela_terceiro_periodo != null ? String(editingItem.valor_parcela_terceiro_periodo) : "",
    valor_fgts: editingItem.valor_fgts != null ? String(editingItem.valor_fgts) : "",
    data_primeira_parcela: editingItem.data_primeira_parcela ? editingItem.data_primeira_parcela.split('T')[0] : "",
    data_ultima_parcela: editingItem.data_ultima_parcela ? editingItem.data_ultima_parcela.split('T')[0] : "",
    tipo_aceite: editingItem.tipo_aceite ?? null,
    valores_extras: editingItem.valores_extras
      ? Object.fromEntries(Object.entries(editingItem.valores_extras).map(([k, v]) => [k, v != null ? String(v) : ""]))
      : {},
  };
}

export function CedenteDialog({ isOpen, onOpenChange, processoId, onSuccess, editingItem = null, temValores = true, camposValores = [], parcelado = true, calculoAutomatico = true, tipoAceitePadrao = null }) {
  // Mode State
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  // Data State
  const [allCedentes, setAllCedentes] = useState([]);
  const [selectedCedente, setSelectedCedente] = useState(() => editingItem?.cedente ?? null);
  const [newCedenteData, setNewCedenteData] = useState({ nome: "", cpf: "" });
  const [dealData, setDealData] = useState(() => initDealData(editingItem));

  // Pagination & Filtering State
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 1000);
  const isDebouncingSearch = debouncedSearchQuery !== searchQuery;

  // Recovery Choice State
  const [showRecoveryChoice, setShowRecoveryChoice] = useState(false);
  const [pendingLinkData, setPendingLinkData] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [isSearching, setIsSearching] = useState(false); // For actual data loading
  const [isSaving, setIsSaving] = useState(false);

  // Auto-calculate deal values (only when calculo_automatico is enabled)
  useEffect(() => {
    if (!calculoAutomatico) return;
    if (!dealData.valor_credito) return;
    const credito = parseFloat(dealData.valor_credito) || 0;
    const fgts = parseFloat(dealData.valor_fgts) || 0;
    const desagio = parseFloat(dealData.percentual_desagio) || 0;
    const sindicato = parseFloat(dealData.valor_sindicato) || 0;
    const contador = parseFloat(dealData.valor_contador) || 0;
    const numParcelas = parseInt(dealData.parcelas) || 0;

    // Crédito Líquido (sem FGTS) — base for proposta/cessão calculation
    // FGTS is excluded: paid directly to worker's conta vinculada by Suzano
    const creditoSemFgts = credito - fgts;

    // 1. Valor Parcelado = valor_credito (full credit, FGTS included when present)
    const parceladoValue = credito;
    const parceladoStr = parceladoValue > 0 ? parceladoValue.toFixed(2) : "";

    // 2. Valor Proposta Final = (creditoSemFgts × (1 - desagio%)) - sindicato - contador - deduções negativas
    const extraDeductions = camposValores
      .filter(c => c.negativo)
      .reduce((acc, campo) => {
        const v = parseFloat((dealData.valores_extras || {})[campo.key] || 0);
        return acc + (isNaN(v) ? 0 : v);
      }, 0);
    const propostaValue = creditoSemFgts > 0
      ? Math.max(0, (creditoSemFgts * (1 - desagio / 100)) - sindicato - contador - extraDeductions)
      : 0;
    const propostaFinalStr = propostaValue > 0 ? propostaValue.toFixed(2) : "";

    // 3. Valor Parcela = valor_credito / parcelas
    let valorParcelaStr = "";
    if (parceladoValue > 0 && numParcelas > 0) {
      valorParcelaStr = (parceladoValue / numParcelas).toFixed(2);
    }

    // Update state if different to avoid infinite loops
    if (
      dealData.valor_proposta_final !== propostaFinalStr ||
      dealData.valor_parcelado !== parceladoStr ||
      dealData.valor_parcela !== valorParcelaStr
    ) {
      setDealData(prev => ({
        ...prev,
        valor_proposta_final: propostaFinalStr,
        valor_parcelado: parceladoStr,
        valor_parcela: valorParcelaStr
      }));
    }
  }, [
    calculoAutomatico,
    dealData.valor_credito,
    dealData.valor_fgts,
    dealData.percentual_desagio,
    dealData.valor_sindicato,
    dealData.valor_contador,
    dealData.parcelas,
    dealData.valor_parcela,
    dealData.valor_parcelado,
    dealData.valor_proposta_final,
    dealData.valores_extras,
    camposValores,
  ]);

  // Initial Data Fetch
  useEffect(() => {
    if (isOpen && !isCreatingNew && allCedentes.length === 0) {
      loadAllCedentes();
    }
  }, [isOpen, isCreatingNew, allCedentes.length]);

  const loadAllCedentes = async () => {
    setIsSearching(true);
    try {
      // Fetch all cedentes for frontend filtering
      const data = await getCedentes("", null, null);
      setAllCedentes(data);
    } catch (e) {
      toast.error("Erro ao carregar substituídos");
    } finally {
      setIsSearching(false);
    }
  };

  const handleConfirm = async (cedenteToLink, dealPayload, options = {}) => {
    if (!cedenteToLink || !cedenteToLink.id) {
      toast.error("Erro ao identificar substituído");
      return;
    }

    try {
      setIsSaving(true);
      await linkCedente(processoId, {
        cedente_id: cedenteToLink.id,
        ...dealPayload,
        ...options
      });

      toast.success(options.restore ? "Vínculo anterior restaurado!" : "Substituído vinculado com sucesso!");
      setShowRecoveryChoice(false);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      // Handle Conflict 409 - Deleted record exists
      if (error.response?.status === 409) {
        setPendingLinkData({ cedenteToLink, dealPayload });
        setIsCreatingNew(false); // Ensure we switch away from registration form
        setShowRecoveryChoice(true);
        return;
      }

      toast.error(error.response?.data?.detail?.message || "Erro ao vincular substituído.");
    } finally {
      setIsSaving(false);
    }
  };

  // Proactive check when a cedente is selected
  useEffect(() => {
    const performCheck = async () => {
      if (selectedCedente && !isCreatingNew && !editingItem) {
        try {
          const { exists } = await checkDeletedLink(processoId, selectedCedente.id);
          if (exists) {
             setPendingLinkData({ cedenteToLink: selectedCedente, dealPayload: {} }); // Mock payload for choice
             setShowRecoveryChoice(true);
          }
        } catch (e) {
          toast.error("Erro ao verificar registro anterior.");
        }
      }
    };
    performCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCedente, isCreatingNew, processoId]);

  const handleRestore = () => {
    if (pendingLinkData) {
      handleConfirm(pendingLinkData.cedenteToLink, pendingLinkData.dealPayload, { restore: true });
    }
  };

  const handleForceNew = () => {
    if (pendingLinkData) {
      handleConfirm(pendingLinkData.cedenteToLink, pendingLinkData.dealPayload, { force_new: true });
    }
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setIsCreatingNew(false);
      setSelectedCedente(null);
      setNewCedenteData({ nome: "", cpf: "" });
      setSearchQuery("");
      setCurrentPage(1);
      setShowRecoveryChoice(false);
      setPendingLinkData(null);
      setDealData(initDealData(null));
    }
  }, [isOpen]);

  // Frontend Filtering
  const filteredCedentes = React.useMemo(() => {
    if (!debouncedSearchQuery) return allCedentes;
    const term = debouncedSearchQuery.toLowerCase();
    const cleanTerm = term.replace(/\D/g, '');

    return allCedentes.filter(c => {
      const nomeMatch = c.nome ? c.nome.toLowerCase().includes(term) : false;
      const cpfMatch = (cleanTerm && c.cpf) ? c.cpf.replace(/\D/g, '').includes(cleanTerm) : false;
      return nomeMatch || cpfMatch;
    });
  }, [allCedentes, debouncedSearchQuery]);

  // Local Pagination
  const paginatedCedentes = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCedentes.slice(start, start + pageSize);
  }, [filteredCedentes, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredCedentes.length / pageSize);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let cedenteToLink = selectedCedente;

      // If creating new, register first
      if (isCreatingNew) {
        if (!newCedenteData.nome || !newCedenteData.cpf) {
          toast.error("Nome e CPF são obrigatórios");
          setIsSaving(false);
          return;
        }

        if (!isValidCPF(newCedenteData.cpf)) {
          toast.error("CPF inválido.");
          setIsSaving(false);
          return;
        }

        const created = await createCedente(newCedenteData);
        if (created && created.id) {
          cedenteToLink = created;
          // Add to local list to keep it updated without a full refetch
          setAllCedentes(prev => [created, ...prev]);
          toast.success("Substituído cadastrado com sucesso!");
        } else {
          throw new Error("Falha ao criar cedente.");
        }
      }

      // Build flat payload for single link endpoint: cedente_id + deal fields
      const payload = {
        valor_credito: dealData.valor_credito ? parseFloat(dealData.valor_credito) : null,
        percentual_desagio: dealData.percentual_desagio ? parseFloat(dealData.percentual_desagio) : null,
        valor_parcelado: dealData.valor_parcelado ? parseFloat(dealData.valor_parcelado) : null,
        parcelas: dealData.parcelas ? parseInt(dealData.parcelas) : null,
        valor_parcela: dealData.valor_parcela ? parseFloat(dealData.valor_parcela) : null,
        valor_proposta_final: dealData.valor_proposta_final ? parseFloat(dealData.valor_proposta_final) : null,
        valor_parcela_primeiro_periodo: dealData.valor_parcela_primeiro_periodo ? parseFloat(dealData.valor_parcela_primeiro_periodo) : null,
        valor_parcela_segundo_periodo: dealData.valor_parcela_segundo_periodo ? parseFloat(dealData.valor_parcela_segundo_periodo) : null,
        valor_parcela_terceiro_periodo: dealData.valor_parcela_terceiro_periodo ? parseFloat(dealData.valor_parcela_terceiro_periodo) : null,
        valor_fgts: dealData.valor_fgts ? parseFloat(dealData.valor_fgts) : null,
        data_primeira_parcela: dealData.data_primeira_parcela || null,
        data_ultima_parcela: dealData.data_ultima_parcela || null,
        tipo_aceite: dealData.tipo_aceite || null,
        valores_extras: (() => {
          const extras = {
            ...Object.fromEntries(
              Object.entries(dealData.valores_extras || {})
                .filter(([, v]) => v !== "" && v != null)
                .map(([k, v]) => [k, parseFloat(v)])
            ),
          };
          if (dealData.valor_sindicato) extras.honorarios_sindicato = parseFloat(dealData.valor_sindicato);
          if (dealData.valor_contador) extras.honorarios_contrat = parseFloat(dealData.valor_contador);
          return Object.keys(extras).length > 0 ? extras : null;
        })(),
      };

      await handleConfirm(cedenteToLink, payload);

    } catch (e) {
      if (e.message.includes("Cedente já cadastrado")) {
        toast.error("Este CPF já está cadastrado no sistema.");
      } else {
        toast.error("Erro: " + e.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingItem ? "Editar Vínculo" : isCreatingNew ? "Criar e Vincular Substituído" : "Vincular Substituído"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
          {/* Left Column */}
          <div className="border p-4 rounded-md flex flex-col gap-4 min-h-[400px]">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">
                {editingItem ? "Substituído" : showRecoveryChoice ? "Ação Requerida" : isCreatingNew ? "Dados do Novo Substituído" : "Selecione o Substituído"}
              </h4>
              {!editingItem && !isCreatingNew && !showRecoveryChoice ? (
                <Button variant="outline" size="xs" className="h-7 text-xs" onClick={() => setIsCreatingNew(true)}>
                  <Plus className="h-3 w-3 mr-1" /> Criar Novo
                </Button>
              ) : !editingItem && isCreatingNew ? (
                <Button variant="ghost" size="xs" className="h-7 text-xs" onClick={() => setIsCreatingNew(false)}>
                  <ArrowLeft className="h-3 w-3 mr-1" /> Voltar
                </Button>
              ) : null}
            </div>

            {editingItem ? (
              <div className="bg-muted border rounded-md p-3 space-y-1">
                <p className="font-medium text-sm">{editingItem.cedente?.nome}</p>
                <p className="text-xs text-muted-foreground">{editingItem.cedente?.cpf}</p>
              </div>
            ) : isCreatingNew ? (
              <CedenteForm data={newCedenteData} onChange={setNewCedenteData} />
            ) : showRecoveryChoice ? (
              <div className="py-6 space-y-4">
                <div className="bg-statusWarningBg border border-statusWarning/20 p-4 rounded-md">
                   <h3 className="text-statusWarning font-medium mb-1 text-sm">Registro Anterior Encontrado</h3>
                   <p className="text-statusWarning text-xs">
                     Este substituído já teve um vínculo com este processo que foi removido.
                     Deseja restaurar os dados anteriores ou criar um vínculo do zero?
                   </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                   <Button
                     variant="outline"
                     className="h-20 flex flex-col gap-1 border-statusWarning/20 hover:bg-statusWarningBg"
                     onClick={handleRestore}
                     disabled={isSaving}
                   >
                     <span className="font-semibold text-statusWarning">Restaurar</span>
                     <span className="text-[10px] text-statusWarning font-normal">Recupera dados históricos</span>
                   </Button>
                   <Button
                     variant="outline"
                     className="h-20 flex flex-col gap-1"
                     onClick={handleForceNew}
                     disabled={isSaving}
                   >
                     <span className="font-semibold text-foreground">Criar Novo</span>
                     <span className="text-[10px] text-muted-foreground font-normal">Inicia vínculo limpo</span>
                   </Button>
                </div>
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground text-xs"
                  onClick={() => setShowRecoveryChoice(false)}
                  disabled={isSaving}
                >
                  Cancelar e voltar
                </Button>
              </div>
            ) : (
              <CedenteSearch
                query={searchQuery}
                setQuery={setSearchQuery}
                cedentes={paginatedCedentes}
                loading={isSearching}
                onSelect={setSelectedCedente}
                selectedId={selectedCedente?.id}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isDebouncing={isDebouncingSearch}
              />
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">
                {tipoAceitePadrao === "cessao" ? "Valores da Cessão" : "Valores da Proposta"}
              </h4>
              <div className="flex items-center gap-1.5">
                {tipoAceitePadrao === "cessao" && (
                  <span className="text-xs bg-statusSuccessBg text-statusSuccess px-2 py-1 rounded font-medium">Cessão</span>
                )}
                {tipoAceitePadrao === "acordo" && (
                  <span className="text-xs bg-statusInfoBg text-statusInfo px-2 py-1 rounded font-medium">Acordo</span>
                )}
                {(editingItem || (selectedCedente && !isCreatingNew)) && <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">Para: {editingItem?.cedente?.nome ?? selectedCedente?.nome}</span>}
                {isCreatingNew && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Novo Cadastro</span>}
              </div>
            </div>

            <DealForm
              data={dealData}
              onChange={setDealData}
              disabled={!editingItem && !selectedCedente && !isCreatingNew}
              showTipoAceite={!!editingItem}
              temValores={temValores}
              camposValores={camposValores}
              parcelado={parcelado}
              calculoAutomatico={calculoAutomatico}
              tipoAceitePadrao={tipoAceitePadrao}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleSave}
            disabled={
              isSaving ||
              (!editingItem && !isCreatingNew && !selectedCedente) ||
              (isCreatingNew && (!newCedenteData?.nome || !newCedenteData?.cpf || newCedenteData.cpf.length < 11))
            }
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editingItem ? "Salvar Alterações" : isCreatingNew ? "Criar e Vincular" : "Vincular Substituído"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
